<script>
import { computed, defineComponent, ref } from "vue";
import { useRoute } from "vue-router";
import { useAdminStore } from "@voix/store/adminStore";
import { usePageStore } from "@voix/store/pageStore";
import { useUserStore } from "@voix/store/userStore";
import MediaManager from "@voix/components/fields/media/media-manager/MediaManager.vue";
import { provideBrowseMode, provideMediaProviders } from "@voix/composables/useMediaManagerInjections";
import SettingsNavigation from "./SettingsNavigation.vue";
import VoixModal from "./VoixModal.vue";
import { navigateTo } from "#imports";
export default defineComponent({
  components: { SettingsNavigation, VoixModal, MediaManager },
  setup(props) {
    const adminStore = useAdminStore();
    const pageStore = usePageStore();
    const goToAdminHome = () => {
      navigateTo({
        name: "voix-settings-en"
      });
    };
    const goToEditor = () => {
      navigateTo({
        name: "voix-studio-en"
      });
    };
    const goToSettings = () => {
      navigateTo({
        name: "voix-settings-en"
      });
    };
    const goToReleases = () => {
      navigateTo({
        name: "voix-settings-releases-en"
      });
    };
    const goToCookbook = () => {
      navigateTo({
        name: "voix-cookbook-en"
      });
    };
    const goToTemplatizer = () => {
      navigateTo({
        name: "templatizer-slices-en"
      });
    };
    const route = useRoute();
    const section = computed(() => {
      if (route.path.includes("studio"))
        return "editor";
      if (route.path.includes("settings"))
        return "settings";
      if (route.path.includes("templatizer"))
        return "templatizer";
      return "unknown";
    });
    const studioEnabled = computed(() => {
      return pageStore.currentPage;
    });
    const userStore = useUserStore();
    const currentUser = computed(() => userStore.currentUser);
    const showLogout = ref(false);
    const logout = () => {
      showLogout.value = false;
      userStore.logout();
      navigateTo({
        path: "/"
      });
    };
    const showSettingsMenu = ref(false);
    const showUserDialog = ref(false);
    const showMediaManager = ref(false);
    const toggleMediaManager = () => {
      showMediaManager.value = true;
    };
    provideMediaProviders();
    provideBrowseMode(true);
    return {
      props,
      adminStore,
      studioEnabled,
      goToAdminHome,
      goToEditor,
      goToSettings,
      goToTemplatizer,
      goToReleases,
      goToCookbook,
      section,
      currentUser,
      logout,
      showSettingsMenu,
      showUserDialog,
      showLogout,
      showMediaManager,
      toggleMediaManager
    };
  }
});
</script>

<template>
  <div>
    <div
      v-if="currentUser"
      class="fixed z-50 left-0 top-0 bottom-0 w-[80px] h-screen flex flex-col justify-between bg-white border-gray-100 shadow pointer-events-auto"
    >
      <div class="flex flex-col space-y-4">
        <button
          class="w-[80px] h-[80px] flex flex-col justify-center items-center voix-admin-bg-dark voix-admin-text-light"
          @click="goToAdminHome"
        >
          <VoixLogo class="w-12 h-12" />
        </button>
        <div
          class="flex justify-center items-center"
          @mouseleave="showSettingsMenu = false"
        >
          <div
            class="relative"
            @mouseenter="showSettingsMenu = true"
            @mouseleave="showSettingsMenu = false"
          >
            <button
              class="w-14 h-14 transition-colors duration-200 rounded-lg flex justify-center items-center"
              :class="{
                'bg-gray-100 hover:bg-gray-200': section !== 'settings',
                'voix-admin-bg-lightest hover:voix-admin-text-lightest': section === 'settings',
              }"
              @click="goToSettings()"
            >
              <Icon class="w-6 h-6 voix-admin-text" name="heroicons:cog-20-solid" />
            </button>
            <Transition
              enter-active-class="transition-opacity duration-300"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-300"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <SettingsNavigation
                v-show="showSettingsMenu"
                class="absolute z-10 left-0 ml-[55px] -translate-y-16"
              />
            </Transition>
          </div>
        </div>
        <div class="flex justify-center items-center">
          <button
            v-voix-popper:#app.right="`<span class='text-xs'>Releases</span>`"
            class="w-14 h-14 transition-colors duration-200 rounded-lg flex justify-center items-center"
            :class="{
              'bg-gray-100 hover:bg-gray-200': section !== 'templatizer',
              'voix-admin-bg-lightest hover:voix-admin-text-lightest': section === 'templatizer',
            }"
            @click="goToReleases()"
          >
            <Icon class="w-6 h-6 voix-admin-text" name="heroicons:cube" />
          </button>
        </div>
        <div class="flex justify-center items-center">
          <button
            v-voix-popper:#app.right="`<span class='text-xs'>Cookbook</span>`"
            class="w-14 h-14 transition-colors duration-200 rounded-lg flex justify-center items-center"
            :class="{
              'bg-gray-100 hover:bg-gray-200': section !== 'templatizer',
              'voix-admin-bg-lightest hover:voix-admin-text-lightest': section === 'templatizer',
            }"
            @click="goToCookbook()"
          >
            <Icon class="w-6 h-6 voix-admin-text" name="heroicons:book-open" />
          </button>
        </div>
        <div class="flex justify-center items-center">
          <button
            v-voix-popper:#app.right="`<span class='text-xs'>Slice Viewer</span>`"
            class="w-14 h-14 transition-colors duration-200 rounded-lg flex justify-center items-center"
            :class="{
              'bg-gray-100 hover:bg-gray-200': section !== 'templatizer',
              'voix-admin-bg-lightest hover:voix-admin-text-lightest': section === 'templatizer',
            }"
            @click="goToTemplatizer()"
          >
            <Icon class="w-6 h-6 voix-admin-text" name="fluent-emoji-high-contrast:pizza" />
          </button>
        </div>
        <div class="flex justify-center items-center">
          <button
            v-voix-popper:#app.right="`<span class='text-xs'>Media Manager</span>`"
            class="w-14 h-14 transition-colors duration-200 rounded-lg flex justify-center items-center"
            :class="{
              'bg-gray-100 hover:bg-gray-200': section !== 'templatizer',
              'voix-admin-bg-lightest hover:voix-admin-text-lightest': section === 'templatizer',
            }"
            @click="toggleMediaManager()"
          >
            <Icon class="w-6 h-6 voix-admin-text" name="heroicons:photo" />
          </button>
        </div>
      </div>
      <div class="flex justify-center items-center" @mouseenter="showUserDialog = true" @mouseleave="showUserDialog = false">
        <div class="relative rounded-full  p-3 mb-4 voix-admin-bg-light voix-admin-text-dark">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-8 h-8">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <div
            :class="{ 'translate-x-[69px] opacity-100': showUserDialog, 'translate-x-[0] opacity-0 pointer-events-none': !showUserDialog }"
            class="absolute bottom-0  left-0 bg-white w-40 flex flex-col border voix-admin-border-lightest rounded divide-y divide-gray-200 transition-all duration-200 ease-out"
          >
            <NuxtLink :to="{ name: 'voix-settings-users-profile-en' }" tag="button" class="p-2 px-4 text-sm text-left voix-admin-text-dark hover:bg-gray-50">
              User Settings
            </NuxtLink>
            <button class="p-2 px-4 text-sm text-left voix-admin-text-dark hover:bg-gray-50" @click="showLogout = true">
              Logout
            </button>
          </div>
        </div>
        <VoixModal :show="showLogout" :can-close="true">
          <div class="bg-white rounded p-4 md:w-[500px] text-base">
            <div>Are you sure you want to Logout?</div>
            <div class="mt-4 flex justify-end space-x-3 text-sm">
              <button class="p-2 px-4 rounded bg-gray-200 text-gray-800" @click="showLogout = false">
                Cancel
              </button>
              <button class="p-2 px-4 rounded bg-gray-200 text-gray-800" @click="logout">
                Confirm
              </button>
            </div>
          </div>
        </VoixModal>
      </div>
    </div>

    <MediaManager v-if="showMediaManager" @close="showMediaManager = false" />
  </div>
</template>
